import { City, Country, State } from "country-state-city";
import { getCountryCode } from "./countryFlag";

export interface CityData {
  name?: string;
  countryCode?: string;
}

/** Option for react-select AsyncSelect (registration checkout / explorer, etc.). */
export interface CityOption {
  value: string;
  label: string;
  city: string;
  country: string;
}

/**
 * If city holds a combined "City, Country" label (e.g. Explorer.City from API),
 * split into separate values. Country parsed from the city string wins over a
 * stale/explicit country (same idea as getCountryFlagUrl combo priority).
 * When city has no comma, the explicit country is kept.
 */
export function splitCityCountry(
  city?: string | null,
  country?: string | null,
): { city: string; country: string } {
  const rawCity = (city ?? "").trim();
  const rawCountry = (country ?? "").trim();
  if (!rawCity.includes(",")) {
    return { city: rawCity, country: rawCountry };
  }
  const i = rawCity.lastIndexOf(",");
  return {
    city: rawCity.slice(0, i).trim(),
    country: rawCity.slice(i + 1).trim() || rawCountry,
  };
}

/** Display label with both city and country when available (no duplicate country). */
export function formatCityCountryLabel(
  city?: string | null,
  country?: string | null,
): string {
  const { city: c, country: co } = splitCityCountry(city, country);
  return [c, co].filter(Boolean).join(", ");
}

/**
 * Extra city labels the package omits (often modeled as states/provinces only),
 * keyed by ISO country code.
 */
const EXTRA_CITIES_BY_COUNTRY_CODE: Record<string, readonly string[]> = {
  TR: ["Istanbul", "İstanbul"],
  // Ensure the metro appears before UK borough rows from country-state-city.
  GB: ["London"],
};

/**
 * UK admin-district rows from country-state-city (often stored as "states"):
 * "London Borough of Brent", "Royal Borough of …", "Borough of Bolton", etc.
 * Also drop London metro aliases so the picker shows plain "London".
 */
function isUkAdminDistrictLabel(name: string): boolean {
  const n = name.trim();
  if (!n) return false;
  if (/\bborough of\b/i.test(n)) return true;
  if (/\bcounty borough\b/i.test(n)) return true;
  if (/\bborough council\b/i.test(n)) return true;
  if (/^london borough\b/i.test(n)) return true;
  if (/^greater london$/i.test(n)) return true;
  if (/^city of london$/i.test(n)) return true;
  if (/^west end of london$/i.test(n)) return true;
  return false;
}

function normalizeForSearch(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase("en-US")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

const citiesByCountryCodeCache = new Map<string, string[]>();

let countryNameByCode: Map<string, string> | null = null;

function getCountryNameByCode(): Map<string, string> {
  if (!countryNameByCode) {
    countryNameByCode = new Map(
      Country.getAllCountries().map((c) => [c.isoCode, c.name]),
    );
  }
  return countryNameByCode;
}

/** City names for one or more country display names (e.g. "United Kingdom"). */
export function getCityNamesForCountries(countryNames: string[]): string[] {
  const codes = new Set<string>();
  for (let i = 0; i < countryNames.length; i++) {
    const code = getCountryCode(countryNames[i]);
    if (code) codes.add(code);
  }
  if (codes.size === 0) return [];

  const names = new Set<string>();
  Array.from(codes).forEach((code) => {
    let cached = citiesByCountryCodeCache.get(code);
    if (!cached) {
      const unique = new Set<string>();
      const fromLib = City.getCitiesOfCountry(code) ?? [];
      for (let i = 0; i < fromLib.length; i++) {
        const n = fromLib[i].name?.trim();
        if (n && !(code === "GB" && isUkAdminDistrictLabel(n))) unique.add(n);
      }
      // Provinces/states are often how major metros are stored (e.g. Istanbul).
      const states = State.getStatesOfCountry(code) ?? [];
      for (let i = 0; i < states.length; i++) {
        const n = states[i].name?.trim();
        if (n && !(code === "GB" && isUkAdminDistrictLabel(n))) unique.add(n);
      }
      const extras = EXTRA_CITIES_BY_COUNTRY_CODE[code];
      if (extras) {
        for (let i = 0; i < extras.length; i++) unique.add(extras[i]);
      }
      cached = Array.from(unique).sort((a, b) => a.localeCompare(b));
      citiesByCountryCodeCache.set(code, cached);
    }
    for (let i = 0; i < cached.length; i++) {
      names.add(cached[i]);
    }
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

const MAX_CITY_OPTIONS = 30;

function searchCityOptions(inputValue: string): CityOption[] {
  const searchTerm = normalizeForSearch(inputValue);
  if (!searchTerm) return [];

  const countryMap = getCountryNameByCode();
  const results: CityOption[] = [];
  const seen = new Set<string>();

  const push = (name: string | undefined, countryCode: string | undefined) => {
    const n = name?.trim();
    const code = countryCode?.trim();
    if (!n || !code) return true;
    if (code === "GB" && isUkAdminDistrictLabel(n)) return true;
    if (!normalizeForSearch(n).includes(searchTerm)) return true;
    const dedupeKey = `${code}|${normalizeForSearch(n)}`;
    if (seen.has(dedupeKey)) return true;
    seen.add(dedupeKey);
    const countryName = countryMap.get(code) || code;
    results.push({
      value: n,
      label: `${n}, ${countryName}`,
      city: n,
      country: countryName,
    });
    return results.length < MAX_CITY_OPTIONS;
  };

  // 1) Curated extras first (London, Istanbul, …) so metros beat borough noise.
  const extraCodes = Object.keys(EXTRA_CITIES_BY_COUNTRY_CODE);
  for (let i = 0; i < extraCodes.length; i++) {
    const code = extraCodes[i];
    const extras = EXTRA_CITIES_BY_COUNTRY_CODE[code];
    for (let j = 0; j < extras.length; j++) {
      if (!push(extras[j], code)) break;
    }
  }

  // 2) States/provinces — covers metros stored as regions (Istanbul, etc.)
  const states = State.getAllStates();
  for (let i = 0; i < states.length; i++) {
    if (!push(states[i].name, states[i].countryCode)) break;
  }

  // 3) Cities from the package (UK borough rows filtered out)
  if (results.length < MAX_CITY_OPTIONS) {
    const cities = City.getAllCities();
    for (let i = 0; i < cities.length; i++) {
      if (!push(cities[i].name, cities[i].countryCode)) break;
    }
  }

  // Prefer exact / prefix matches (e.g. "London" over "Londonderry").
  results.sort((a, b) => {
    const an = normalizeForSearch(a.city);
    const bn = normalizeForSearch(b.city);
    const aExact = an === searchTerm ? 0 : an.startsWith(searchTerm) ? 1 : 2;
    const bExact = bn === searchTerm ? 0 : bn.startsWith(searchTerm) ? 1 : 2;
    if (aExact !== bExact) return aExact - bExact;
    return a.label.localeCompare(b.label);
  });

  return results.slice(0, MAX_CITY_OPTIONS);
}

/**
 * Search cities worldwide for AsyncSelect.
 * Returns a Promise (preferred by react-select) and also supports the callback API.
 */
export function loadCityOptions(
  inputValue: string,
  callback?: (options: readonly CityOption[]) => void,
): Promise<readonly CityOption[]> {
  const promise = new Promise<readonly CityOption[]>((resolve) => {
    setTimeout(() => {
      try {
        resolve(searchCityOptions(inputValue));
      } catch (e) {
        console.error("Error searching cities:", e);
        resolve([]);
      }
    }, 150);
  });

  if (callback) {
    void promise.then(callback);
  }
  return promise;
}
